/* Inertial wheel-zoom damping — port of the toolkit's map-zoom-damping.js
   (map-polish module). Magic Mouse / trackpad flicks keep emitting decaying
   wheel events after the finger stops, and Leaflet's ScrollWheelZoom rounds
   ANY leftover delta up to a full zoom level per debounce batch (Math.ceil
   against zoomSnap), so one forceful flick lands several consecutive zooms
   the visitor never intended.

   Three dampers, toolkit numbers verbatim (assets/js/map-zoom-damping.js):
     1. wheelDebounceTime 40 -> 100: the flick and most of its tail merge
        into fewer zoom batches;
     2. wheelPxPerZoomLevel 60 -> 120: a level takes twice the scroll delta;
     3. a patched _performZoom on the map's own scrollWheelZoom handler:
        batches under DEAD_ZONE_PX are discarded outright; within COOLDOWN_MS
        of a wheel zoom, batches weaker than STRONG_PX are discarded — that
        is the decaying momentum tail. A visitor still actively scrolling
        produces strong batches and zooms straight through the cooldown.

   Unlike the toolkit — a deferred script that has to catch maps via Leaflet
   prototype hooks because the plugin builds the map in a closure — the
   widget owns map creation, so this applies directly at init. Leaflet reads
   both wheel options at event time; no re-init is needed. */

const DEBOUNCE_MS = 100
const PX_PER_LEVEL = 120
const DEAD_ZONE_PX = 20
const COOLDOWN_MS = 400
const STRONG_PX = 120

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyZoomDamping(map: any): void {
  map.options.wheelDebounceTime = DEBOUNCE_MS
  map.options.wheelPxPerZoomLevel = PX_PER_LEVEL

  const handler = map.scrollWheelZoom
  if (!handler || typeof handler._performZoom !== 'function') return

  const native = handler._performZoom
  let lastZoomAt = 0
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler._performZoom = function (this: any) {
    const strength = Math.abs(this._delta)
    const discard =
      strength < DEAD_ZONE_PX ||
      (Date.now() - lastZoomAt < COOLDOWN_MS && strength < STRONG_PX)
    if (discard) {
      this._delta = 0
      this._startTime = null
      return
    }
    lastZoomAt = Date.now()
    return native.call(this)
  }
}
